<template>

	<div>

		<video :key="selectedProject" class="background-video" autoplay muted loop aria-hidden="true">
			<source :src="getBackgroundVideo + '_c.mp4'" type="video/mp4" />
			<source :src="getBackgroundVideo + '.webm'" type="video/webm" />
			Your browser does not support the video tag.
		</video>

		<div class="icon-row">

			<picture
				v-for="(project, index) in reorderedProjects"
				:key="index"
				:title="project.name.toUpperCase()"
				class="project-image"
				:class="selectedProject == index ? 'active' : ''"
				@click="setCurrentProject(index)"
			>

				<source :srcset="getLogo(project) + '.webp'" type="image/webp"/>
				<source :srcset="getLogo(project) + '.jpg'" type="image/jpg"/>

				<img :src="`/src/projects/${project.link}/${project.link}_logo.jpg`" alt=""/>

			</picture>

		</div>

		<div class="main-container">

			<div class="preview">

				<video :key="selectedProject" class="project-video" autoplay muted loop aria-hidden="true">

					<source :src="getVideo + '.webm'" type="video/webm" />
					<source :src="getVideo + '_c.mp4'" type="video/mp4" />

					Your browser does not support the video tag.

				</video>

			</div>

			<div class="description">

				<h2 style="margin-bottom: 0; margin-top: 0; display: inline-block;">
					{{ currentProject.name.toUpperCase() }}
					<span style="color: gray;">
						{{ " (" + currentProject.year + ")" }}
					</span>
				</h2>

				<p>

					<span
						v-for="(platform, index) in currentProject.platforms"
						:key="index"
					>

						<a
							v-if="platform == PROJECT_PLATFORM.STEAM"
							class="steamLink"
							:href="currentProject.steamLink"
						>
							<Icon :icon="getPlatformIcon(platform)" />
							{{ " " + platform }}
							<span v-if="index !== currentProject.platforms.length - 1"> | </span>
						</a>

						<a
							v-else-if="platform == PROJECT_PLATFORM.GITHUB"
							class="steamLink"
							:href="currentProject.gitHubLink"
						>
							<Icon :icon="getPlatformIcon(platform)" />
							{{ " " + platform }}
							<span v-if="index !== currentProject.platforms.length - 1"> | </span>
						</a>

						<a
							v-else-if="platform == PROJECT_PLATFORM.ITCH"
							class="steamLink"
							:href="currentProject.itchLink"
						>
							<Icon :icon="getPlatformIcon(platform)" />
							{{ " " + platform }}
							<span v-if="index !== currentProject.platforms.length - 1"> | </span>
						</a>

						<span v-else>
							<Icon :icon="getPlatformIcon(platform)" />
							{{ " " + platform }}
							<span v-if="index !== currentProject.platforms.length - 1"> | </span>
						</span>

					</span>

				</p>

				<p>
					<Icon :icon="getEngineIcon(currentProject.engine)" />
					{{ " " + currentProject.engine }} in {{ currentProject.language }}
				</p>

				<button
					v-for="(role, index) in currentProject.me?.roles"
					:key="index"
					class="role"
					:style="'background: ' + currentProject.accentColor"
				>
					{{ role.toUpperCase() }}
				</button>

				<p class="game-description">{{ currentProject.description }}</p>

				<div class="task-overview">

					<strong>Task Overview</strong>

					<ul>
						<li class="game-description game-list"
							v-for="(task, index) in currentProject.tasks"
							:key="index"
						>
							<component :is="task" />
						</li>
					</ul>

				</div>

			</div>

		</div>

		<MSFS
			v-if="currentProject.link == 'msfs'"
			:projectData="currentProject"
		/>

		<CoopScoop
			v-if="currentProject.link == 'coopscoop'"
			:projectData="currentProject"
		/>

		<GudenuffEngine
			v-if="currentProject.link == 'gudenuffengine'"
			:projectData="currentProject"
		/>

		<InSheepsClothing
			v-if="currentProject.link == 'insheepsclothing'"
			:projectData="currentProject"
		/>

		<GallusEngine
			v-if="currentProject.link == 'gallusengine'"
			:projectData="currentProject"
		/>

	</div>

</template>

<script lang="ts">
import { defineComponent, computed, ref } from "vue";
import { useRoute } from "vue-router";

import { PROJECTS } from "@/data";
import { Project, PROJECT_PLATFORM, GAME_ENGINE } from "@/classes";

import Icon from "./Icon/Icon.vue";

import MSFS from "./projects/MSFS.vue";
import GudenuffEngine from "./projects/GudenuffEngine.vue";
import CoopScoop from "./projects/CoopScoop.vue";
import InSheepsClothing from "./projects/InSheepsClothing.vue";
import GallusEngine from "./projects/GallusEngine.vue";

import EventBus from "../EventBus";
import { useRouter } from "vue-router";

export default defineComponent({
	name: "Home",

	components: {
		Icon,
		MSFS,
		GudenuffEngine,
		CoopScoop,
		InSheepsClothing,
		GallusEngine
	},

	props: {
		startingProject: {
			type: String,
			default: ""
		}
	},

	setup(props) {

		const route = useRoute();
		const router = useRouter(); 
		
		const el = document.getElementById("seo-fallback");
		if (el) {
			el.remove();
		}

		const selectedProject = ref(0);

		const reorderedProjects = computed((): Project[] =>
		{
			const query = route.query;
			const key = Object.keys(query)[0]?.toLowerCase() ?? "";

			const matches = (project: Project): boolean =>
			{
				switch (key)
				{
					case "unity":
					{
						return project.engine === GAME_ENGINE.UNITY_ENGINE;
					}

					case "unreal":
					{
						return project.engine === GAME_ENGINE.UNREAL_ENGINE;
					}

					case "csharp":
					case "c#":
					{
						return project.language.toLowerCase().includes("c#");
					}

					case "cpp":
					case "c++":
					{
						return project.language.toLowerCase().includes("c++");
					}

					default:
					{
						return false;
					}
				}
			};

			return [...PROJECTS].sort((a, b) =>
			{
				return (matches(a) ? 0 : 1) - (matches(b) ? 0 : 1);
			});
		});

		const currentProject = computed((): Project =>
		{
			return reorderedProjects.value[selectedProject.value];
		});

		const getVideo = computed((): string =>
		{
			return `/${currentProject.value.link}/src/mp4/showcase_video`;
		});

		const getBackgroundVideo = computed((): string =>
		{
			return `/${currentProject.value.link}/src/mp4/background_video`;
		});

		function getLogo(project: Project): string
		{
			return `/${project.link}/src/img/logo`;
		}

		function getPlatformIcon(platform: PROJECT_PLATFORM): string
		{
			switch (platform)
			{
				case PROJECT_PLATFORM.WINDOWS:
				{
					return "windows";
				}

				case PROJECT_PLATFORM.PS4:
				case PROJECT_PLATFORM.PS5:
				{
					return "playstation";
				}

				case PROJECT_PLATFORM.VR:
				{
					return "vr";
				}

				case PROJECT_PLATFORM.STEAM:
				{
					return "steam";
				}

				case PROJECT_PLATFORM.ITCH:
				{
					return "itch";
				}

				case PROJECT_PLATFORM.GITHUB:
				{
					return "github";
				}

				case PROJECT_PLATFORM.XBOX:
				{
					return "xbox";
				}

				default:
				{
					return "";
				}
			}
		}

		function getEngineIcon(engine: GAME_ENGINE): string
		{
			switch (engine)
			{
				case GAME_ENGINE.UNITY_ENGINE:
				{
					return "unity";
				}

				case GAME_ENGINE.UNREAL_ENGINE:
				{
					return "unreal";
				}

				default:
				{
					return "home";
				}
			}
		}

		function setCurrentProject(index: number, useRouter = true): void
		{
			selectedProject.value = index;
			EventBus.accentColor = currentProject.value.accentColor;
			if (useRouter)
			{
				router.replace(`/${currentProject.value.link}`);
			}
		}

		let project = 0;

		if (props.startingProject)
		{
			project = PROJECTS.findIndex((p: Project) =>
			{
				return p.link === props.startingProject;
			});
		}

		setCurrentProject(project, false);

		return {
			selectedProject,
			reorderedProjects,
			currentProject,
			getVideo,
			getBackgroundVideo,
			getLogo,
			getPlatformIcon,
			getEngineIcon,
			setCurrentProject,
			PROJECT_PLATFORM,
			GAME_ENGINE
		};
	}
});
</script>

<style lang="scss" scoped>

@import url("/public/src/css/default.scss");
@import url("/public/src/css/home.scss");

</style>